'use client';
import { Dispatch, useEffect, useState } from 'react';
import Image from 'next/image';

import './WishlistIcon.style.scss';
import HeartFilled from 'public/img/icon/heart-filled-black.svg';
import HeartOutline from 'public/img/icon/heart-outline-black.svg';

import { ProductCardData } from '@/types/Product';
import { sleep } from '@/util/misc';

import Button from '@/components/common/Button/Button';
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner';
import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';
import { useLoggedInUser } from '@/components/hook/user';
import { useWishlist } from '@/components/hook/wishlist';
import { EllaAction } from '../Ella/Ella';
import { LoginFormValues } from '@/components/form/LoginForm/LoginForm';
import { useModal } from '@/components/hook/modal';
import LoginModal from '@/components/modal/LoginModal/LoginModal';
import toast from 'react-hot-toast';
import {
  addToWishlistIOSDeepLink,
  removeFromWishlistIOSDeepLink
} from '@/util/iosDeepLinks';

type WishlistIconProps = {
  className?: string;
  data: ProductCardData;
  disabled?: boolean;
  wishlistAnalytics?: (data: ProductCardData) => void;
  useTemporary?: boolean;
  dispatch?: Dispatch<EllaAction>;
};

function WishlistIcon(props: WishlistIconProps) {
  const {
    className = '',
    data,
    disabled,
    wishlistAnalytics,
    useTemporary = false,
    dispatch
  } = props;
  const [loadingWishlistOp, setLoadingWishlistOp] = useState<boolean>(false);
  const [isWishListingFromLogin, setIsWishListingFromLogin] = useState(false);
  const user = useLoggedInUser();
  const { isIOSWebView } = useContextBroker();
  const { actions, value: wishlistValue } = useWishlist();
  const modalTools = useModal();

  useEffect(() => {
    if (isWishListingFromLogin) {
      handleWishlisting('ADD');
      setIsWishListingFromLogin(false);
    }
  }, [isWishListingFromLogin]);

  const itemIsWishlisted = user
    ? wishlistValue.wishlistAsMap[data.sku ?? data.item_group_id]
    : useTemporary
    ? wishlistValue.tempWishlistAsMap[data.sku ?? data.item_group_id]
    : false;

  const handleWishlisting = async (action: 'ADD' | 'REMOVE') => {
    setLoadingWishlistOp(true);

    try {
      if (action === 'ADD') {
        if (useTemporary) {
          actions.addToTempWishlist(data.sku ?? data.item_group_id);
          toast('Item added to temporary wishlist.');
        } else {
          await actions.addToWishlist(data.sku ?? data.item_group_id);
          toast('Item added to wishlist.');
        }
        if (dispatch) {
          dispatch({
            type: 'WISHLIST_ADD_ITEM',
            payload: {
              wishlistUpdated: true
            }
          });
        }
      } else {
        if (useTemporary) {
          await actions.removeFromTempWishlist(data.sku ?? data.item_group_id);
          toast('Item removed from temporary wishlist.');
        } else {
          const wishlistedItem = wishlistValue.wishlist.find(
            (item) => item.sku === (data.sku ?? data.item_group_id)
          );

          if (!wishlistedItem) {
            console.error(
              `Unable to remove ${
                data.sku ?? data.item_group_id
              } from wishlist because it doesn't seem to exist in current wishlist`
            );
            return;
          }

          await actions.removeFromWishlist(wishlistedItem.wishlistItemId);
          toast('Item removed from wishlist.');
        }
      }

      if (wishlistAnalytics) {
        wishlistAnalytics(data);
      }

      // Only refresh wishlist for non-temporary operations
      if (!useTemporary) {
        // Wait to allow request, OpenSearch indices etc to resolve/update
        await sleep(1000);
        await actions.refreshWishlist();

        // Wait again and refresh in the background to be extra sure wishlist is updated..
        await sleep(500);
        actions.refreshWishlist();
      }
    } finally {
      setLoadingWishlistOp(false);
    }
  };

  const handleWishlistClick = async (action: 'ADD' | 'REMOVE') => {
    if (!user && !useTemporary) {
      modalTools.showModal(
        <LoginModal
          headline='Login to add to wishlist'
          initialValues={{ username: '', password: '' }}
          onClose={modalTools.closeModal}
          onSubmitSuccess={(_: LoginFormValues, resp: unknown) => {
            setIsWishListingFromLogin(true);
            modalTools.closeModal();
          }}
          onNavigateClick={modalTools.closeModal}
        />,
        { topClassName: 'bg-gold' }
      );
      return;
    }

    handleWishlisting(action);
  };

  const getWishlistIcon = () => {
    if (loadingWishlistOp) {
      return (
        <LoadingSpinner
          className='WishlistIcon'
          size={16}
          gradient='from 90deg,rgba(39, 174, 96, 0) 0deg,rgba(149, 149, 149, 0) 0.036deg,rgba(149, 149, 149, 1) 360deg'
        />
      );
    }

    const ariaLabel = itemIsWishlisted
      ? 'Wishlist icon. This item is wishlisted'
      : 'Wishlist icon. This item is not wishlisted';
    const wishlistIconToShow = itemIsWishlisted ? HeartFilled : HeartOutline;
    const wishlistAction = itemIsWishlisted ? 'REMOVE' : 'ADD';

    return (
      <>
        {isIOSWebView ? (
          <a
            className='WishlistIcon cursor'
            href={
              itemIsWishlisted
                ? removeFromWishlistIOSDeepLink(data.sku)
                : addToWishlistIOSDeepLink(data.sku)
            }
          >
            <Image
              alt='wishlist heart icon'
              src={wishlistIconToShow}
            />
          </a>
        ) : (
          <Button
            className='WishlistIcon cursor transparent'
            aria-label={ariaLabel}
            onClick={(e) => {
              // We still need these for the ProductHorizontalCard
              // https://vivrelle.atlassian.net/browse/VIVE-1197
              e.preventDefault();
              e.stopPropagation();
              handleWishlistClick(wishlistAction);

              if (wishlistAnalytics) {
                wishlistAnalytics(data);
              }
            }}
            disabled={disabled}
          >
            <Image
              alt='wishlist heart icon'
              src={wishlistIconToShow}
            />
          </Button>
        )}
      </>
    );
  };

  return <>{getWishlistIcon()}</>;
}

export default WishlistIcon;
